/**
 * @description Serviço para excluir eventos do Google Calendar
 * @module DeleteEvent
 * @since Março 2024
 */

import axios from 'axios';
import { endpoints } from '../../configs/Settings';
import { useAuth } from '../../utils/useAuth';
import { RefreshTokenGoogle } from './RefreshTokenGoogle';
import { useSessionContext } from '../../contexts/SessionContext';

interface DeleteEventParams {
  eventId: string;
  accessTokenGoogle: string;
  refreshToken: string;
}

/**
 * Exclui um evento do Google Calendar
 * @param params - Parâmetros necessários para excluir o evento
 * @returns Promise com o status da exclusão
 * @throws Error se a exclusão falhar
 */
export function useDeleteEvent() {
  const { setAccessTokenGoogle } = useSessionContext();

  const deleteEvent = async ({ 
    eventId, 
    accessTokenGoogle, 
    refreshToken 
  }: DeleteEventParams) => {
    if (!accessTokenGoogle) {
      throw new Error('Token de acesso do Google não disponível');
  }

  try {
    const response = await axios.delete(
      `${endpoints.GOOGLE}/calendar/v3/calendars/primary/events/${eventId}`,
      {
        headers: {
            'Authorization': await useAuth.getUserToken(),
          'Content-Type': 'application/json',
            'x-custom-google-auth': `Bearer ${accessTokenGoogle}`
        }
      }
    );

      return { success: true, status: response.status };
    } catch (error: any) {
      console.error('Erro detalhado:', error.response?.data || error.message);
      
      if (error.response?.status === 401) {
        try {
          // Tenta renovar o token do Google
          const googleTokenData = await RefreshTokenGoogle(refreshToken);
          
          // Atualiza o token no contexto
          setAccessTokenGoogle(googleTokenData.access_token);
          
          // Tenta excluir o evento novamente com o novo token
          return await deleteEvent({
            eventId,
            accessTokenGoogle: googleTokenData.access_token,
            refreshToken
          });
        } catch (refreshError) {
          console.error('Erro ao renovar token do Google:', refreshError);
          throw new Error('Falha ao renovar token do Google');
        }
      }
      
      // Se o evento não for encontrado (já foi excluído ou nunca existiu), consideramos como sucesso
      if (error.response?.status === 410) {
        return { success: true, status: 404, message: 'Evento não encontrado' };
      }
      
      throw new Error(`Falha ao excluir evento do Google Calendar: ${error.message}`);
  }
  };

  return { deleteEvent };
} 